import { action, computed, makeObservable, observable } from 'mobx';
import client from 'src/services/client';
import { IResponseIntegrations, TIntegrationsAvailability, integrationCast } from 'src/types/integrations';
import { LoadingStatus } from 'src/types/main';
import { Integrations, defaultIntegrationsAvailability } from '~/constants/integrations';
import { getAbortController, isCanceledRequest } from '~/helpers';
import { appStore } from '~/mobx/appStore';
import logger from '~/services/logger';

class IntegrationsStore {
  public initial: boolean = true;

  constructor() {
    makeObservable(this);
  }

  private getIntegrationsAbortController = new AbortController();

  @observable private integrationsAvailability: TIntegrationsAvailability = defaultIntegrationsAvailability;
  @observable public loading: LoadingStatus = LoadingStatus.Init;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loading);
  }

  @computed public get integrationsState(): Partial<TIntegrationsAvailability> {
    return {
      [Integrations.ADJUST]: false,
      [Integrations.APPLE_PAY]: this.integrationsAvailability[Integrations.APPLE_PAY],
      [Integrations.CHECKOUT]: this.integrationsAvailability[Integrations.CHECKOUT],
      [Integrations.CONNECT]: this.integrationsAvailability[Integrations.CONNECT],
      [Integrations.DOOK]: false,
      [Integrations.FOODICS]: this.integrationsAvailability[Integrations.FOODICS],
      [Integrations.GOOGLE_MAPS]: this.integrationsAvailability[Integrations.GOOGLE_MAPS],
      [Integrations.GOOGLE_TAG_MANAGER]: this.integrationsAvailability[Integrations.GOOGLE_TAG_MANAGER],
      [Integrations.KUDU]: false,
      [Integrations.MARN]: false,
      [Integrations.T360]: this.integrationsAvailability[Integrations.T360],
      [Integrations.TAKER_GO]: Boolean(appStore.settings?.takergo_enabled && this.integrationsAvailability[Integrations.TAKER_GO]),
      [Integrations.YALLOW]: false,
    };
  }

  @action public getIntegrations = async () => {
    this.getIntegrationsAbortController.abort();
    this.getIntegrationsAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const response: IResponseIntegrations = await client.get('v3/integrations', { signal: this.getIntegrationsAbortController.signal });

      this.integrationsAvailability = integrationCast(response);

      this.initial = false;
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'integrationsStore getIntegrations', showModal: true });
    }
  };
}

const integrationsStore = new IntegrationsStore();
export default integrationsStore;
